import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DroughtAnalyticsView } from '../../analytics/drought-view.ts';
import {
  analyzeLongDroughts,
  formatSpacingSequence,
} from '../../analytics/long-droughts.ts';
import {
  DEFAULT_LONG_DROUGHT_THRESHOLD,
  DROUGHT_ANALYTICS_TARGETS,
  LONG_DROUGHT_QUICK_THRESHOLDS,
} from '../../shared/constants.ts';
import {
  formatDecimal,
  formatHours,
  formatInteger,
  formatPercent,
  formatPercentagePoints,
  formatUtcStamp,
} from '../../shared/format.ts';
import { fetchJson } from '../lib/api.ts';
import { parseDroughtTargetParam, parseLongDroughtThresholdParam } from '../lib/targets.ts';
import { DroughtHistogram } from '../components/DroughtHistogram.tsx';
import { LongDroughtSpacingChart } from '../components/LongDroughtSpacingChart.tsx';
import { LongDroughtTimeline } from '../components/LongDroughtTimeline.tsx';
import { SampleBadge } from '../components/SampleBadge.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';

export function DroughtsPage() {
  const [params, setParams] = useSearchParams();
  const rawTarget = params.get('target');
  const rawLong = params.get('long');
  const target = parseDroughtTargetParam(rawTarget);
  const threshold = parseLongDroughtThresholdParam(rawLong);
  const [view, setView] = useState<DroughtAnalyticsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [distributionView, setDistributionView] = useState<'all' | 'tail'>('all');
  const [referenceMode, setReferenceMode] = useState<'observed' | 'reference'>('observed');

  useEffect(() => {
    const next = new URLSearchParams(params);
    let changed = false;
    if (rawTarget !== String(target)) {
      next.set('target', String(target));
      changed = true;
    }
    if (rawLong !== null && rawLong !== String(threshold)) {
      next.set('long', String(threshold));
      changed = true;
    }
    if (changed) {
      setParams(next, { replace: true });
    }
  }, [rawTarget, rawLong, target, threshold, params, setParams]);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    void fetchJson<DroughtAnalyticsView>('/api/analytics/drought', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selectedTarget: target }),
    })
      .then((value) => {
        if (!cancelled) {
          setView(value);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Failed to load drought analysis');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const selected = view?.selected;
  const longDroughts = useMemo(() => {
    if (!selected) {
      return null;
    }
    return analyzeLongDroughts(selected.completedDroughts ?? [], selected.hitSequences ?? [], threshold);
  }, [selected, threshold]);

  const stats = useMemo(() => {
    if (!selected) {
      return [];
    }
    const dist = selected.distribution;
    return [
      { label: 'Current drought', value: formatInteger(dist.current.currentDroughtRounds) },
      {
        label: 'Current percentile',
        value:
          dist.current.percentilePercent === null ? '—' : formatPercent(dist.current.percentilePercent / 100, 1),
      },
      { label: 'Mean drought', value: formatDecimal(dist.mean, 2) },
      { label: 'Median drought', value: formatDecimal(dist.median, 2) },
      { label: 'P75', value: formatDecimal(dist.p75, 2) },
      { label: 'P90', value: formatDecimal(dist.p90, 2) },
      { label: 'P95', value: formatDecimal(dist.p95, 2) },
      { label: 'P99', value: formatDecimal(dist.p99, 2) },
      { label: 'Longest completed', value: formatInteger(dist.longestCompletedDrought) },
      { label: 'Completed samples', value: formatInteger(dist.completedDroughtCount) },
      { label: 'Hit rate', value: formatPercent(dist.empiricalHitFrequency, 2) },
    ];
  }, [selected]);

  const writeParams = (nextTarget: number, nextThreshold: number) => {
    const next = new URLSearchParams();
    next.set('target', String(nextTarget));
    if (nextThreshold !== DEFAULT_LONG_DROUGHT_THRESHOLD || rawLong !== null) {
      next.set('long', String(nextThreshold));
    }
    setParams(next);
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Drought analysis</h1>
        </div>
        <label className="flex items-center gap-2 text-sm">
          Target
          <select
            className="rounded border border-line bg-panel px-2 py-1"
            value={String(target)}
            onChange={(event) => writeParams(Number(event.target.value), threshold)}
          >
            {DROUGHT_ANALYTICS_TARGETS.map((value) => (
              <option key={value} value={value}>
                {value}x
              </option>
            ))}
          </select>
        </label>
      </header>

      {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
      {!error && !view ? <StatusMessage kind="loading">Loading drought analysis</StatusMessage> : null}

      {view && selected && longDroughts ? (
        <>
          {selected.distribution.completedDroughtCount === 0 ? (
            <StatusMessage kind="empty">No observations are available for this target.</StatusMessage>
          ) : null}

          <section className="rounded border border-line bg-panel p-4">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Current state</h2>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4 lg:grid-cols-6">
              {stats.map((item) => (
                <div key={item.label}>
                  <dt className="text-xs text-muted">{item.label}</dt>
                  <dd className="text-sm font-medium">{item.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="rounded border border-line bg-panel p-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
                Drought length distribution
              </h2>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <ToggleButton
                  active={distributionView === 'all'}
                  onClick={() => setDistributionView('all')}
                  label="All"
                />
                <ToggleButton
                  active={distributionView === 'tail'}
                  onClick={() => setDistributionView('tail')}
                  label="Long tail"
                />
                {target === 2 ? (
                  <>
                    <ToggleButton
                      active={referenceMode === 'observed'}
                      onClick={() => setReferenceMode('observed')}
                      label="Observed only"
                    />
                    <ToggleButton
                      active={referenceMode === 'reference'}
                      onClick={() => setReferenceMode('reference')}
                      label="Observed vs independent reference"
                    />
                  </>
                ) : null}
              </div>
            </div>
            <div className="mt-3">
              <DroughtHistogram
                rows={selected.frequency?.rows ?? []}
                median={selected.distribution.median}
                p90={selected.distribution.p90}
                p95={selected.distribution.p95}
                currentDrought={selected.distribution.current.currentDroughtRounds}
                view={distributionView}
                tailFrom={threshold}
                showIndependentReference={target === 2 && referenceMode === 'reference'}
              />
            </div>
            {target === 2 && referenceMode === 'reference' ? (
              <p className="mt-2 max-w-3xl text-xs text-muted">{selected.frequency.independentReferenceNote}</p>
            ) : null}
          </section>

          <section className="rounded border border-line bg-panel p-4">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Long droughts</h2>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="flex items-center gap-2 text-sm">
                Long drought {'>='}
                <input
                  aria-label="Long drought threshold"
                  className="w-20 rounded border border-line bg-page px-2 py-1"
                  type="number"
                  min={1}
                  step={1}
                  value={threshold}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (Number.isInteger(next) && next >= 1) {
                      writeParams(target, next);
                    }
                  }}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                {LONG_DROUGHT_QUICK_THRESHOLDS.map((value) => (
                  <ToggleButton
                    key={value}
                    active={threshold === value}
                    onClick={() => writeParams(target, value)}
                    label={`${value}+`}
                  />
                ))}
              </div>
            </div>
            <p className="mt-3 text-sm">
              Events found: {formatInteger(longDroughts.eventCount)}
            </p>
            <p className="mt-1 max-w-3xl text-xs text-muted">
              Completed droughts with length at least {formatInteger(threshold)}. Threshold is inclusive. This is a
              location map, not a forecast.
            </p>
            <div className="mt-4">
              <h3 className="text-sm font-medium">Long drought timeline</h3>
              <div className="mt-2">
                <LongDroughtTimeline
                  events={longDroughts.events}
                  firstSequence={selected.firstSequence}
                  lastSequence={selected.lastSequence}
                />
              </div>
            </div>
          </section>

          <section className="rounded border border-line bg-panel p-4">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Long drought spacing</h2>
            <p className="mt-2 max-w-3xl text-xs text-muted">
              Primary spacing is start-to-start: current start sequence minus previous long-drought start sequence.
              Spacing is omitted across a continuity break.
            </p>
            <div className="mt-4">
              <h3 className="text-sm font-medium">Spacing between long droughts</h3>
              <div className="mt-2">
                <LongDroughtSpacingChart events={longDroughts.events} />
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4 lg:grid-cols-6">
              <Metric label="Intervals" value={formatInteger(longDroughts.spacing.intervalCount)} />
              <Metric label="Mean rounds" value={formatDecimal(longDroughts.spacing.mean, 1)} />
              <Metric label="Median rounds" value={formatDecimal(longDroughts.spacing.median, 1)} />
              <Metric label="Std. dev." value={formatDecimal(longDroughts.spacing.stddev, 1)} />
              <Metric label="Minimum" value={formatInteger(longDroughts.spacing.minimum)} />
              <Metric label="Maximum" value={formatInteger(longDroughts.spacing.maximum)} />
              <Metric
                label="Coeff. of variation"
                value={formatDecimal(longDroughts.spacing.coefficientOfVariation, 2)}
              />
              <Metric label="Mean hours" value={formatHours(longDroughts.spacing.meanHours)} />
              <Metric label="Median hours" value={formatHours(longDroughts.spacing.medianHours)} />
            </dl>
            <p className="mt-3 text-sm text-muted">{longDroughts.spacing.sampleSizeNote}</p>
            {longDroughts.spacing.orderedStartToStartRounds.length > 0 ? (
              <p className="mt-2 text-sm">
                Start-to-start spacing:{' '}
                <span className="font-medium">{formatSpacingSequence(longDroughts.spacing.orderedStartToStartRounds)}</span>
              </p>
            ) : null}

            {threshold >= 11 && longDroughts.countsByLength.length > 0 ? (
              <div className="mt-4">
                <h3 className="text-sm font-medium">Counts by exact drought length</h3>
                <table className="mt-2 text-sm">
                  <thead className="text-xs text-muted">
                    <tr>
                      <th className="pr-6 text-left font-medium">Length</th>
                      <th className="text-left font-medium">Occurrences</th>
                    </tr>
                  </thead>
                  <tbody>
                    {longDroughts.countsByLength.map((row) => (
                      <tr key={row.droughtLength}>
                        <td className="pr-6 py-1">{row.droughtLength}</td>
                        <td className="py-1">{formatInteger(row.occurrenceCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-[900px] w-full text-left text-sm">
                <thead className="border-b border-line text-xs text-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium">Event</th>
                    <th className="px-3 py-2 font-medium">Length</th>
                    <th className="px-3 py-2 font-medium">Start seq</th>
                    <th className="px-3 py-2 font-medium">End seq</th>
                    <th className="px-3 py-2 font-medium">Start time</th>
                    <th className="px-3 py-2 font-medium">Rounds since previous</th>
                    <th className="px-3 py-2 font-medium">Hours since previous</th>
                  </tr>
                </thead>
                <tbody>
                  {longDroughts.events.map((event) => (
                    <tr key={event.eventIndex} className="border-b border-line/70">
                      <td className="px-3 py-2">{event.eventIndex}</td>
                      <td className="px-3 py-2">{formatInteger(event.lengthRounds)}</td>
                      <td className="px-3 py-2">{formatInteger(event.startSequence)}</td>
                      <td className="px-3 py-2">{formatInteger(event.endSequence)}</td>
                      <td className="px-3 py-2">{formatUtcStamp(event.startTimestampUtc)}</td>
                      <td className="px-3 py-2">
                        {event.roundsSincePreviousStart === null
                          ? '—'
                          : formatInteger(event.roundsSincePreviousStart)}
                      </td>
                      <td className="px-3 py-2">
                        {event.hoursSincePreviousStart === null
                          ? '—'
                          : formatHours(event.hoursSincePreviousStart)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded border border-line bg-panel">
            <div className="border-b border-line px-4 py-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Drought continuation</h2>
              <p className="mt-2 max-w-3xl text-xs text-muted">
                Next-round hit % is the historical frequency with which the target was reached on the round
                immediately after a drought had already reached D misses. It is descriptive, not a forecast.
              </p>
            </div>
            <div className="table-scroll">
              <table className="min-w-[1100px] w-full text-left text-sm">
                <thead className="border-b border-line text-xs text-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium">Reached D</th>
                    <th className="px-3 py-2 font-medium">Samples</th>
                    <th className="px-3 py-2 font-medium">Ended next</th>
                    <th className="px-3 py-2 font-medium">Continued</th>
                    <th className="px-3 py-2 font-medium">Next-round hit %</th>
                    <th className="px-3 py-2 font-medium">Wilson 95% CI</th>
                    <th className="px-3 py-2 font-medium">Baseline hit %</th>
                    <th className="px-3 py-2 font-medium">Difference</th>
                    <th className="px-3 py-2 font-medium">Hit within 2</th>
                    <th className="px-3 py-2 font-medium">Hit within 3</th>
                    <th className="px-3 py-2 font-medium">Hit within 5</th>
                    <th className="px-3 py-2 font-medium">Hit within 10</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.continuation.rows.map((row) => (
                    <tr key={row.droughtDepth} className="border-b border-line/70 align-top">
                      <td className="px-3 py-2">{row.droughtDepth}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col gap-1">
                          <span>{formatInteger(row.reachedAtLeastD)}</span>
                          <SampleBadge n={row.reachedAtLeastD} />
                        </div>
                      </td>
                      <td className="px-3 py-2">{formatInteger(row.endedNext)}</td>
                      <td className="px-3 py-2">{formatInteger(row.continued)}</td>
                      <td className="px-3 py-2">
                        {formatPercent(row.historicalNextRoundHitRate, 1)}
                        {row.insufficientSample ? (
                          <div className="text-[10px] uppercase tracking-wide text-warn">INSUFFICIENT SAMPLE</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        {row.historicalNextRoundHitWilson
                          ? `${formatPercent(row.historicalNextRoundHitWilson.lower, 1)}–${formatPercent(row.historicalNextRoundHitWilson.upper, 1)}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2">{formatPercent(row.baselineHitFrequency, 1)}</td>
                      <td className="px-3 py-2">{formatPercentagePoints(row.differenceVsBaseline, 1)}</td>
                      <HorizonCell cell={row.forward.next2} />
                      <HorizonCell cell={row.forward.next3} />
                      <HorizonCell cell={row.forward.next5} />
                      <HorizonCell cell={row.forward.next10} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function ToggleButton(props: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className={`rounded border px-2 py-1 text-sm ${
        props.active ? 'border-ink bg-soft font-medium' : 'border-line bg-page text-muted'
      }`}
      onClick={props.onClick}
    >
      {props.label}
    </button>
  );
}

function Metric(props: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{props.label}</dt>
      <dd className="text-sm font-medium">{props.value}</dd>
    </div>
  );
}

function HorizonCell(props: {
  cell:
    | {
        historicalHitFrequency: number | null;
        qualifyingEpisodes: number;
      }
    | undefined;
}) {
  if (!props.cell) {
    return <td className="px-3 py-2">—</td>;
  }
  return (
    <td className="px-3 py-2">
      <div>{formatPercent(props.cell.historicalHitFrequency, 1)}</div>
      <div className="text-xs text-muted">n={formatInteger(props.cell.qualifyingEpisodes)}</div>
    </td>
  );
}
